import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild, ViewEncapsulation, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { CoreService } from '../../services/core.service';
import { AppSettings } from '../../app.config';
import { navItems } from './vertical/sidebar/sidebar-data';
import { NavItem } from './vertical/sidebar/nav-item/nav-item';
import { NavService } from '../../services/nav.service';
import { NgScrollbarModule } from 'ngx-scrollbar';
import {
  AppSearchDialogComponent,
  HeaderComponent,
} from './vertical/header/header.component';
import { AppHorizontalHeaderComponent } from './horizontal/header/header.component';
import { AppHorizontalSidebarComponent } from './horizontal/sidebar/sidebar.component';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { MaterialModule } from '../../material.module';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { environment } from 'apps/portaladmin/src/environments/environment';
import { AuthService } from '@auth-suite-services/services';
import { filterNavItemsByScopes } from '../../utils/filter-nav-items.util';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';
@Component({
  selector: 'app-full',
  templateUrl: './full.component.html',
  standalone: true,
  imports: [
    NgScrollbarModule,
    HeaderComponent,
    AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent,
    SidebarComponent,
    AppBreadcrumbComponent,
    AppSearchDialogComponent,
    CustomizerComponent,
    MaterialModule,
    RouterModule,
    CommonModule,
    AppNavItemComponent,
  ],
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit, OnDestroy {
  test: Date = new Date();
  public env = environment;
  showBreadcrumb = true;

  navItems: NavItem[] = [];
  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;
  //get options from service
  options = this.settings.getOptions();
  navopt = this.navService.showClass;
  private layoutChangesSubscription = Subscription.EMPTY;
  private routerSubscription = Subscription.EMPTY;
  private authSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  /** Zoom scale (1 = 100%). Used to counter-scale horizontal sidebar so it never zooms with the page. */
  horizontalZoomScale = 1;
  /** Base height (px) of the horizontal sidebar at 100% zoom. */
  readonly horizontalSidebarBaseHeight = 72;
  private viewportResizeListener: (() => void) | null = null;
  private windowResizeListener: (() => void) | null = null;
  /** devicePixelRatio at load; used to detect desktop browser zoom (Ctrl +/-). */
  private initialDevicePixelRatio = 1;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private navService: NavService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[MOBILE_VIEW];

        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);
  }

  ngOnInit(): void {
    this.refreshNavItems();
    this.authSubscription = this.authService.userProfile$.subscribe(() => {
      this.refreshNavItems();
      this.cdr.markForCheck();
    });
    this.updateShowBreadcrumb();
    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.updateShowBreadcrumb());
    this.initialDevicePixelRatio = Math.max(1, window.devicePixelRatio || 1);
    this.updateHorizontalZoomScale();
    const vv = window.visualViewport;
    const onZoomChange = () => {
      this.updateHorizontalZoomScale();
      this.cdr.markForCheck();
    };
    if (vv) {
      this.viewportResizeListener = onZoomChange;
      vv.addEventListener('resize', this.viewportResizeListener);
      vv.addEventListener('scroll', this.viewportResizeListener);
    }
    this.windowResizeListener = onZoomChange;
    window.addEventListener('resize', this.windowResizeListener);
  }

  // Filter SideNav by Amber Auth0 scopes. 18/09/2026 CN
  private refreshNavItems(): void {
    this.navItems = filterNavItemsByScopes(navItems, (scopes) =>
      this.authService.userHasScopes(scopes)
    );
  }

  private updateHorizontalZoomScale(): void {
    const vv = window.visualViewport;
    // visualViewport.scale is 1 on desktop zoom; devicePixelRatio changes with Ctrl+ zoom
    const vvScale = vv?.scale;
    const dprScale = this.initialDevicePixelRatio > 0
      ? window.devicePixelRatio / this.initialDevicePixelRatio
      : 1;
    const scale = (typeof vvScale === 'number' && vvScale > 0 && vvScale !== 1)
      ? vvScale
      : dprScale;
    this.horizontalZoomScale = Math.max(0.25, Math.min(4, scale));
  }

  private updateShowBreadcrumb(): void {
    this.showBreadcrumb = !this.router.url.includes('dashboards/dashboard1');
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
    this.authSubscription.unsubscribe();
    const vv = window.visualViewport;
    if (vv && this.viewportResizeListener) {
      vv.removeEventListener('resize', this.viewportResizeListener);
      vv.removeEventListener('scroll', this.viewportResizeListener);
    }
    if (this.windowResizeListener) {
      window.removeEventListener('resize', this.windowResizeListener);
    }
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }
}
